/**
 * Toast Notification Helper
 * ESM wrapper around the global `window.showToast` API defined in
 * ``templates/components/_toast_messages.html``.
 *
 * See ``docs/TOAST/TOAST_UX_RULES.md`` for the full rules. TL;DR:
 *  - default `showToast(msg, type)` is **route-scoped**: it disappears
 *    on navigation (hx-boost / Back / Forward / bfcache).
 *  - `showToastOnNextPage(msg, type)` defers the toast to the next
 *    page load (sessionStorage, consumed once).
 *  - `showToast(msg, type, { persistAcrossNavigation: true })` opts
 *    out of route-scoping for rare cross-page operations (e.g. an
 *    async import that's still running).
 *  - Server-side flows should prefer ``messages.success(request, …)
 *    + redirect(...)`` — that's the canonical "intentional cross-route
 *    toast" and is fully handled by ``HtmxMessagesMiddleware``.
 */

/**
 * Show a toast notification. Route-scoped by default (cleared on nav).
 *
 * @param {string} message - Message to display.
 * @param {'success'|'error'|'warning'|'info'} [type='info'] - Toast type.
 * @param {object} [opts]
 * @param {boolean} [opts.persistAcrossNavigation=false] - When true,
 *     the toast is tagged ``data-persistent="1"`` and survives the
 *     navigation cleanup. Use sparingly — see TOAST_UX_RULES.md § 2.3.
 * @returns {{dismiss: () => void}} Handle for programmatic dismissal.
 */
export function showToast(message, type = 'info', opts = undefined) {
  if (typeof window.showToast === 'function') {
    return window.showToast(message, type, opts) || { dismiss: () => {} };
  }

  // Fallback: minimal inline rendering when the global helper hasn't
  // been initialised yet (e.g. very early page boot, or this module
  // imported in an isolated test harness). Mirrors the look-and-feel
  // of the canonical helper without the navigation-aware machinery.
  const container = document.getElementById('toast-container');
  if (!container) {
    if (type === 'error') {
      console.error('[Toast]', message);
      alert(message);
    } else {
      console.warn('[Toast]', message);
    }
    return { dismiss: () => {} };
  }

  const bgColors = {
    success: 'bg-green-100 dark:bg-green-800',
    error: 'bg-red-100 dark:bg-red-800',
    warning: 'bg-amber-100 dark:bg-amber-800',
    info: 'bg-blue-100 dark:bg-blue-800',
  };
  const textColors = {
    success: 'text-green-500 dark:text-green-200',
    error: 'text-red-500 dark:text-red-200',
    warning: 'text-amber-500 dark:text-amber-200',
    info: 'text-blue-500 dark:text-blue-200',
  };
  const icons = {
    success: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>',
    error: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>',
    warning: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>',
    info: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
  };

  const toast = document.createElement('div');
  toast.className =
    'pointer-events-auto flex items-center w-full max-w-sm p-4 bg-white rounded-lg shadow-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700 transform transition-all duration-300 -translate-y-2 opacity-0';
  toast.setAttribute('role', 'alert');
  if (opts && opts.persistAcrossNavigation === true) {
    toast.setAttribute('data-persistent', '1');
  }

  toast.innerHTML = `
    <div class="inline-flex items-center justify-center shrink-0 w-8 h-8 rounded-lg ${bgColors[type] || bgColors.info} ${textColors[type] || textColors.info}">
      ${icons[type] || icons.info}
    </div>
    <div class="ms-3 text-sm font-medium text-gray-700 dark:text-gray-200">${escapeHtml(message)}</div>
    <button type="button" data-toast-close="1" class="ms-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700">
      <span class="sr-only">Close</span>
      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
    </button>
  `;

  container.appendChild(toast);
  requestAnimationFrame(() => {
    toast.classList.remove('-translate-y-2', 'opacity-0');
  });

  const dismiss = () => {
    if (!toast.parentElement) return;
    toast.classList.add('-translate-y-2', 'opacity-0');
    setTimeout(() => {
      if (toast.parentElement) toast.remove();
    }, 300);
  };

  const closeBtn = toast.querySelector('[data-toast-close]');
  if (closeBtn) closeBtn.addEventListener('click', dismiss);

  if (type !== 'error') setTimeout(dismiss, 4000);

  return { dismiss, el: toast };
}

/**
 * Defer a toast until the next page load. Use for full-reload flows
 * (location.href = ..., form.submit(), etc.). Stored in sessionStorage
 * and consumed exactly once on the destination page.
 *
 * For server-side flows that already use ``messages.success() +
 * redirect(...)``, you don't need this — the messages framework
 * already handles the cross-route case correctly.
 */
export function showToastOnNextPage(message, type = 'success') {
  if (typeof window.showToastOnNextPage === 'function') {
    window.showToastOnNextPage(message, type);
    return;
  }
  try {
    sessionStorage.setItem('toast_message', message);
    sessionStorage.setItem('toast_type', type);
  } catch (_e) {
    console.warn('[toast] sessionStorage unavailable, falling back to immediate toast');
    showToast(message, type);
  }
}

/**
 * Remove all non-persistent toasts immediately. Called automatically
 * on navigation (see ``frontend/src/app.js``); rarely needed manually.
 */
export function clearToasts() {
  if (typeof window.clearToasts === 'function') {
    window.clearToasts();
    return;
  }
  const container = document.getElementById('toast-container');
  if (!container) return;
  container.querySelectorAll(':scope > div:not([data-persistent="1"])').forEach((n) => n.remove());
}

function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
